import { useEffect, useState } from 'react'
import { PAGES } from '@/constants/pages'
import { PageLayout } from '@/pages/PageLayout'
import { PlatformDevicesService } from '@/services/PlatformDevicesService'
import PlatformDeviceTable from '@/components/PlatformDeviceTable'
import { PlatformDevice } from '@/models/platformDevice'
import { useColumnResizing } from '@/hooks/useColumnResizing'
import { usePlatformDeviceFilters } from '@/hooks/usePlatformDeviceFilters'
import './PlatformDevicesPage.css'
import PlatformDeviceFiltersComponent, { PlatformDeviceFilters } from '@/components/PlatformDeviceFilters'
import { usePlatformDeviceSorting } from '@/hooks/usePlatformDeviceSorting'
import { usePagination } from '@/hooks/usePagination'
import PaginationControls from '@/components/PaginationControls'
import { useLoading } from '@/contexts/LoadingContext'

export default function PlatformDevicesPage() {
  const [devices, setDevices] = useState<PlatformDevice[]>([])
  const { showLoader, hideLoader } = useLoading()

  const { filters, filteredDevices, handleFilterChange, clearAllFilters } = usePlatformDeviceFilters(devices)
  const { sortColumn, sortDirection, sortedDevices, handleSort } = usePlatformDeviceSorting(filteredDevices)
  const { tableRef, columnWidths, handleResizeStart } = useColumnResizing()

  const {
      currentPage,
      pageSize,
      totalPages,
      paginatedItems: paginatedDevices,
      goToPage,
      nextPage,
      previousPage,
      changePageSize,
      totalItems
    } = usePagination(sortedDevices, 100)

  useEffect(() => {
    loadDevices();
  }, [])

  const onFilterChange = (key: keyof PlatformDeviceFilters, value: string | string[]) => {
    handleFilterChange(key, value);
  };

  const loadDevices = async () => {
      try {
        showLoader()
        const devices = (await PlatformDevicesService.getAll()).devices
  
        if (devices == null || devices.length == 0) { 
          setDevices([]);
          return;
        }
        
        setDevices(devices)
      } catch (error) {
        console.error('Failed to load devices:', error)
      } finally {
        hideLoader()
      }
  }

  return (
    <PageLayout
      title={PAGES.DEVICE_EXPORT.title}
      heading={PAGES.DEVICE_EXPORT.heading}
    >

      <PlatformDeviceFiltersComponent
          filters={filters}
          onFilterChange={onFilterChange}
          onClearAllFilters={clearAllFilters}
          devices={devices}
          filteredDevices={filteredDevices}
      />

      {devices.length === 0 ? (
          <div className="no-devices">No devices found.</div>
        ) : (
          <>
            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={goToPage}
                onPageSizeChange={changePageSize}
                onNextPage={nextPage}
                onPreviousPage={previousPage}
            />

            <PlatformDeviceTable
              ref={tableRef}
              devices={paginatedDevices}
              columnWidths={columnWidths}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onResizeStart={handleResizeStart}
            />
          </>
        )
      }
    </PageLayout>
  ); 
}